import React, { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styles from './diary-justify-absences.module.css';
import { subMonths, format, startOfYear } from 'date-fns';

// Components
import BaseContentContainer from '../../components/base-content-container/base-content-container';
import ButtonsBar from '../../components/buttons-bar/buttons-bar';
import ErrorComponent from '../../components/error/error';
import Loading from '../../components/loading/loading';
import SubmitButton from '../../components/submit-button/submit-button';
import AbsenceSelector from '../../components/absence-selector/absence-selector';

// Models
import {
  BaseContentContainerActionButtonPosition,
  BaseContentContainerBiggerSide,
  BaseContentContainerFormat,
} from '../../models/base-content-container';

// Providers
import { GeneralContext } from '../../providers/general-provider';

// Services
import AbsenceService from '../../services/absence-service';
import DiaryService from '../../services/diary-service';
import SchoolService from '../../services/school-service';
import StorageService from '../../services/storage-service';
import FileStorageService from '../../services/file-storage-service';

// Helpers
import {
  getAbsenceReasonOptions,
  formatAbsenceDate,
} from '../../helpers/absence-helper';

const ALLOWED_SICK_NOTE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf', 'doc', 'docx'];
const MAX_SICK_NOTE_SIZE = 25 * 1024 * 1024; // 25 MB

const DiaryJustifyAbsences = () => {
  const { classroomUuid } = useParams<{ classroomUuid: string }>();
  const { setToastContent } = useContext(GeneralContext);

  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [absenceService, setAbsenceService] = useState<AbsenceService | null>(null);
  const [absences, setAbsences] = useState<Absence[]>([]);
  const [selectedAbsences, setSelectedAbsences] = useState<string[]>([]);
  const [selectedReason, setSelectedReason] = useState<AbsenceReason>('HealthIssuesWithSickNote');
  const [periodFilter, setPeriodFilter] = useState<'lastMonth' | 'all'>('lastMonth');
  const [classroomName, setClassroomName] = useState<string>('');
  const [details, setDetails] = useState<string>('');
  const [groupBy, setGroupBy] = useState<'student' | 'date'>('student');
  const [sickNoteFiles, setSickNoteFiles] = useState<Record<string, File>>({});
  const [fileStorageService, setFileStorageService] = useState<FileStorageService | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const getDateRange = () => {
    const today = new Date();
    const endDate = format(today, 'yyyy-MM-dd');
    const yearStart = startOfYear(today);
    const yearStartDate = format(yearStart, 'yyyy-MM-dd');

    let startDate: string;

    if (periodFilter === 'lastMonth') {
      // Subtrai 1 mês da data atual (date-fns lida com casos de borda automaticamente)
      // Ex: 31 de março -> 28 de fevereiro (ou 29 em ano bissexto)
      const lastMonth = subMonths(today, 1);
      const calculatedStartDate = format(lastMonth, 'yyyy-MM-dd');

      // Garantir que startDate não seja menor que o início do ano corrente
      startDate = calculatedStartDate < yearStartDate ? yearStartDate : calculatedStartDate;
    } else {
      // 'all' = início do ano corrente
      startDate = yearStartDate;
    }

    return { startDate, endDate };
  };

  const loadAbsences = () => {
    if (!absenceService) return;

    setLoading(true);
    const { startDate, endDate } = getDateRange();
    absenceService
      .listClassroomAbsences(classroomUuid, startDate, endDate)
      .then((data) => {
        // Sempre mostrar apenas não justificadas
        const filtered = data.filter(a => !a.justification);
        setAbsences(filtered);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    loadAbsences();
  }, [periodFilter, absenceService]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const schoolId = new StorageService().getEmployee().schoolId;

    new SchoolService()
      .getSchoolById(schoolId)
      .then((school) => {
        setAbsenceService(new AbsenceService(school.uuid));
        setFileStorageService(new FileStorageService(school.uuid));

        // Só para o título: a turma sem nome não impede justificar.
        new DiaryService(school.uuid)
          .listPendingStudents(classroomUuid)
          .then((pending) => setClassroomName(pending.classroomName || ''))
          .catch(() => {});
      })
      .catch(() => setError(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Remove arquivos de faltas que deixaram de estar selecionadas
  useEffect(() => {
    setSickNoteFiles((current) => {
      const next: Record<string, File> = {};
      selectedAbsences.forEach((id) => {
        if (current[id]) next[id] = current[id];
      });
      return next;
    });
  }, [selectedAbsences]);

  const handleSelectionChange = (selectedIds: string[]) => {
    setSelectedAbsences(selectedIds);
  };

  const handleSickNoteFileChange = (absenceId: string, file: File | null) => {
    if (!file) {
      removeSickNoteFile(absenceId);
      return;
    }

    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    if (!ALLOWED_SICK_NOTE_EXTENSIONS.includes(extension)) {
      setToastContent({
        isOpen: true,
        text: `Formato não permitido. Use: ${ALLOWED_SICK_NOTE_EXTENSIONS.join(', ')}.`,
        type: 'warning',
      });
      return;
    }

    if (file.size > MAX_SICK_NOTE_SIZE) {
      setToastContent({
        isOpen: true,
        text: 'O arquivo deve ter no máximo 25 MB.',
        type: 'warning',
      });
      return;
    }

    setSickNoteFiles((current) => ({ ...current, [absenceId]: file }));
  };

  const removeSickNoteFile = (absenceId: string) => {
    setSickNoteFiles((current) => {
      const next = { ...current };
      delete next[absenceId];
      return next;
    });
  };

  const justifyWithSickNotes = async () => {
    if (!fileStorageService) {
      setToastContent({
        isOpen: true,
        text: 'Não foi possível carregar os dados da escola. Tente novamente.',
        type: 'error',
      });
      return;
    }

    setSubmitting(true);

    const withFile = selectedAbsences.filter((id) => sickNoteFiles[id]);
    const withoutFile = selectedAbsences.filter((id) => !sickNoteFiles[id]);
    const failed: string[] = [];
    let succeeded = 0;

    for (const id of withFile) {
      try {
        const studentUuid = absences.find((absence) => absence.id === id).student.uuid;
        const fileId = await fileStorageService.uploadSickNote(sickNoteFiles[id], studentUuid);
        await absenceService.justifyAbsence(id, {
          reason: selectedReason,
          sickNoteFileId: fileId,
        });
        succeeded++;
      } catch {
        failed.push(id);
      }
    }

    if (withoutFile.length > 0) {
      try {
        await absenceService.justifyAbsences({
          absenceIds: withoutFile,
          reason: selectedReason,
        });
        succeeded += withoutFile.length;
      } catch {
        failed.push(...withoutFile);
      }
    }

    setSubmitting(false);

    if (failed.length === 0) {
      setToastContent({
        isOpen: true,
        text: `${succeeded} falta(s) justificada(s) com sucesso!`,
        type: 'success',
      });
    } else if (succeeded > 0) {
      setToastContent({
        isOpen: true,
        text: `${succeeded} falta(s) justificada(s); ${failed.length} falharam.`,
        type: 'warning',
      });
    } else {
      setToastContent({
        isOpen: true,
        text: 'Houve um erro ao justificar as faltas.',
        type: 'error',
      });
    }

    setSelectedAbsences([]);
    setSickNoteFiles({});
    setDetails('');
    loadAbsences();
  };

  const handleJustify = () => {
    if (selectedAbsences.length === 0) {
      setToastContent({
        isOpen: true,
        text: 'Selecione pelo menos uma falta para justificar.',
        type: 'warning',
      });
      return;
    }

    if (selectedReason === 'Other' && !details.trim()) {
      setToastContent({
        isOpen: true,
        text: 'O campo de detalhes é obrigatório quando o motivo é "Outros".',
        type: 'warning',
      });
      return;
    }

    // Atestado só pode ser anexado via justificativa individual
    if (selectedReason === 'HealthIssuesWithSickNote') {
      justifyWithSickNotes();
      return;
    }

    const data: JustifyAbsencesRequest = {
      absenceIds: selectedAbsences,
      reason: selectedReason,
      ...(selectedReason === 'Other' && { details: details.trim() }),
    };

    absenceService
      .justifyAbsences(data)
      .then(() => {
        setToastContent({
          isOpen: true,
          text: `${selectedAbsences.length} falta(s) justificada(s) com sucesso!`,
          type: 'success',
        });
        setSelectedAbsences([]);
        setDetails('');
        loadAbsences();
      })
      .catch(() => {
        setToastContent({
          isOpen: true,
          text: 'Houve um erro ao justificar as faltas.',
          type: 'error',
        });
      });
  };

  if (error) return <ErrorComponent type='contentFull' />;

  if (loading) return <Loading type='contentFull' />;

  const getTitle = () => {
    if (selectedAbsences.length === 0) return 'Selecione as faltas';
    if (selectedAbsences.length === 1) return '1 falta selecionada';
    return `${selectedAbsences.length} faltas selecionadas`;
  };

  return (
    <BaseContentContainer
      format={BaseContentContainerFormat.doubleWithActionButtons}
      biggerSide={BaseContentContainerBiggerSide.right}
      actionButtonPosition={BaseContentContainerActionButtonPosition.bottom}
    >
      {/* Formulário - Lado Esquerdo */}
      <div className={styles.formContainer}>
        <h2>{getTitle()}</h2>

        {/* Filtros de Data */}
        <div className={styles.filterSection}>
          <h3>Filtrar por Período</h3>

          <select
            value={periodFilter}
            onChange={(e) => setPeriodFilter(e.target.value as 'lastMonth' | 'all')}
          >
            <option value="lastMonth">Último mês</option>
            <option value="all">Todos</option>
          </select>
        </div>

        {/* Agrupar por */}
        <div className={styles.filterSection}>
          <h3>Agrupar por:</h3>

          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as 'student' | 'date')}
          >
            <option value="student">Aluno</option>
            <option value="date">Data</option>
          </select>
        </div>

        {/* Motivo de Justificativa */}
        <div className={styles.filterSection}>
          <h3>Motivo da Justificativa</h3>

          <select
            value={selectedReason}
            onChange={(e) => setSelectedReason(e.target.value as AbsenceReason)}
          >
            {getAbsenceReasonOptions().map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {selectedReason === 'Other' && (
            <div className={styles.detailsField}>
              <label htmlFor="details">Detalhes (obrigatório):</label>
              <textarea
                id="details"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="Descreva o motivo da justificativa..."
                rows={4}
              />
            </div>
          )}
        </div>

        {/* Upload de atestado por falta (opcional) */}
        {selectedReason === 'HealthIssuesWithSickNote' && selectedAbsences.length > 0 && (
          <div className={styles.filterSection}>
            <h3>Atestados (opcional)</h3>
            <div className={styles.sickNoteSection}>
              {absences
                .filter((a) => selectedAbsences.includes(a.id))
                .map((absence) => {
                  const file = sickNoteFiles[absence.id];
                  const label = `${absence.student.name || 'Aluno'} — ${formatAbsenceDate(absence.date)}`;

                  return (
                    <div key={absence.id} className={styles.sickNoteRow}>
                      <label>{label}</label>
                      {file ? (
                        <div className={styles.fileSelected}>
                          <span className={styles.fileName}>{file.name}</span>
                          <span
                            className={styles.removeFile}
                            onClick={() => removeSickNoteFile(absence.id)}
                          >
                            remover
                          </span>
                        </div>
                      ) : (
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                          onChange={(e) =>
                            handleSickNoteFileChange(absence.id, e.target.files?.[0] || null)
                          }
                        />
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>

      {/* AbsenceSelector - Lado Direito */}
      <AbsenceSelector
        absences={absences}
        selectedAbsences={selectedAbsences}
        onSelectionChange={handleSelectionChange}
        classroomName={classroomName}
        groupBy={groupBy}
      />

      {/* Botões */}
      <ButtonsBar pattern='together' sideAlign='right' verticalAlign='bottom'>
        <Link to={`/diarios/turma/${classroomUuid}`}>
          <SubmitButton value='cancelar' variant='terciary' />
        </Link>
        <SubmitButton
          value={submitting ? 'enviando...' : 'justificar selecionadas'}
          variant='primary'
          onClick={handleJustify}
          disabled={submitting || selectedAbsences.length === 0 || (selectedReason === 'Other' && !details.trim())}
        />
      </ButtonsBar>
    </BaseContentContainer>
  );
};

export default DiaryJustifyAbsences;
